// Import required components and hooks
import React, { useState, useContext } from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { useMutation, gql } from '@apollo/client'
import readXlsxFile from 'read-excel-file'
import { DialogContext } from '../context/DialogContext.js'
import UserContext from '../context/UserContext.js'
import { Box, LinearProgress, TextField, Typography, Divider } from '@mui/material'
import TagsInput from './dialoginputs/TagsInput.js'
import UsersInput from './dialoginputs/UsersInput.js'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import languages, { getLanguageAcronym } from '../languages.js'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'

// GraphQL mutation for adding sentences
const ADD_SENTENCE_MUTATION = gql`
  mutation AddUserSentence(
    $username: String!
    $sourceText: String!
    $targetText: String!
    $sourceLang: String!
    $targetLang: String!
    $tags: [String]!
    $sharedWith: [String]!
  ) {
    AddUserSentence(
      username: $username
      sourceText: $sourceText
      targetText: $targetText
      sourceLang: $sourceLang
      targetLang: $targetLang
      tags: $tags
      sharedWith: $sharedWith
    ) {
      sentence {
        id
        text
        lang
      }
      translations {
        id
        text
        lang
      }
    }
  }
`

// Create the AddSentenceDialog component
function AddSentencesExcelDialog() {
  const { dialogStates, closeDialog } = useContext(DialogContext)
  const { username } = useContext(UserContext)
  const [file, setFile] = useState(null)
  const [pastedData, setPastedData] = useState('')
  const [tags, setTags] = useState([])
  const [users, setUsers] = useState(['David'])
  const [uploadStatus, setUploadStatus] = useState('idle')
  const [sourceLanguage, setSourceLanguage] = useState(Object.keys(languages)[0])
  const [targetLanguage, setTargetLanguage] = useState(Object.keys(languages)[5])

  const getPlaceholder = (source, target) => {
    const sName = languages[source] || 'Target'
    const tName = languages[target] || 'Source'
    
    if (sName === 'Turkish' && tName === 'English') {
      return `Merhaba, Hello\nTeşekkürler, Thanks\n\n(You can separate with commas or paste directly from Excel)`
    }
    if (sName === 'Spanish' && tName === 'English') {
      return `Hola, Hello\nGracias, Thanks\n\n(You can separate with commas or paste directly from Excel)`
    }
    
    return `first word in ${sName}, translation in ${tName}\nsecond word in ${sName}, translation in ${tName}\n\n(You can separate with commas or paste directly from Excel)`
  }

  const handleSourceLanguageChange = (event) => {
    setSourceLanguage(event.target.value)
  }

  const handleTargetLanguageChange = (event) => {
    setTargetLanguage(event.target.value)
  }

  // GraphQL mutation hook
  const [addUserSentence, { loading, error }] = useMutation(ADD_SENTENCE_MUTATION)

  // const handleSourceLanguageChange = (event) => {
  //   setSourceLanguage(event.target.value)
  // }

  // const handleTargetLanguageChange = (event) => {
  //   setTargetLanguage(event.target.value)
  // }

  // Function to handle file selection
  const handleFileChange = (event) => {
    setFile(event.target.files[0])
  }

  // Function to process and upload the Excel file
  const handleFileUpload = () => {
    if (!file && !pastedData.trim()) return;
    
    setUploadStatus('loading')
    
    if (file) {
      if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')) {
        readXlsxFile(file).then((rows) => {
          processRows(rows)
        }).catch((error) => {
          console.error('Excel Parsing failed:', error)
          setUploadStatus('error')
        })
      } else if (file.name.toLowerCase().endsWith('.csv')) {
        const reader = new FileReader()
        reader.onload = (e) => {
          const text = e.target.result;
          const rows = parseTabularData(text, ',')
          processRows(rows)
        }
        reader.onerror = () => setUploadStatus('error')
        reader.readAsText(file)
      }
    } else if (pastedData.trim() !== '') {
      const rows = parseTabularData(pastedData, '\t')
      processRows(rows)
    }
  }

  const parseTabularData = (text, defaultSeparator = '\t') => {
    const lines = text.split('\n').filter(line => line.trim() !== '')
    return lines.map(line => {
      // Determine separator for this line (tabs prioritize over commas)
      const separator = line.includes('\t') ? '\t' : (line.includes(',') ? ',' : defaultSeparator)
      return line.split(separator).map(cell => cell.trim().replace(/^"|"$/g, ''))
    })
  }

  const processRows = (rows) => {
    if (!rows || rows.length === 0) {
      setUploadStatus('error')
      return
    }

    // Process all data rows, assuming headers are obsolete due to dropdowns
    const uploadPromises = rows.filter(r => r[0] && r[1]).map((row) => {
      const sourceText = row[0]
      const targetText = row[1]
      return addUserSentence({
        variables: {
          username: username,
          sourceText: sourceText,
          targetText: targetText,
          sourceLang: sourceLanguage,
          targetLang: targetLanguage,
          tags: tags,
          sharedWith: users
        }
      })
    })

    Promise.all(uploadPromises)
      .then(() => setUploadStatus('completed'))
      .catch((error) => {
        console.error('Upload failed:', error)
        setUploadStatus('error')
      })
  }

  const handleClose = () => {
    closeDialog('dialogAddSentencesExcel')
    setFile(null)
    setPastedData('')
    setUploadStatus('idle')
  }

  return (
    <Dialog open={dialogStates['dialogAddSentencesExcel']} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Bulk Add Sentences</DialogTitle>
      <DialogContent>
        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>Translation Direction</Typography>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} gap={2}>
            <Select 
              labelId="source-language-label" 
              id="sourceLanguage" 
              value={sourceLanguage} 
              onChange={handleSourceLanguageChange} 
              fullWidth
            >
              {Object.entries(languages).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              ))}
            </Select>
            
            <ArrowForwardIcon color="action" />
            
            <Select 
              labelId="target-language-label" 
              id="targetLanguage" 
              value={targetLanguage} 
              onChange={handleTargetLanguageChange} 
              fullWidth
            >
              {Object.entries(languages).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Typography variant="subtitle2" gutterBottom>Option 1: Upload a File (.xlsx, .xls, .csv)</Typography>
          <input type="file" accept=".xlsx, .xls, .csv" onChange={handleFileChange} />
          
          <Box my={2}>
            <Divider>OR</Divider>
          </Box>

          <Typography variant="subtitle2" gutterBottom>Option 2: Paste Tabular Data / CSV</Typography>
          <TextField
            multiline
            rows={5}
            fullWidth
            variant="outlined"
            placeholder={getPlaceholder(sourceLanguage, targetLanguage)}
            value={pastedData}
            onChange={(e) => setPastedData(e.target.value)}
          />

          <Box mt={3}>
            <TagsInput selectedTags={setTags} fullWidth variant="outlined" id="tags" name="tags" placeholder="Add Tags" label="Tags" />
          </Box>

          <Box mt={2} mb={2}>
            <UsersInput selectedUsers={setUsers} users={users} fullWidth variant="outlined" id="users" name="users" placeholder="Type username" label="Share with" />
          </Box>
          
          {uploadStatus === 'loading' && <Box mt={2}><LinearProgress /></Box>}
          {uploadStatus === 'completed' && <Typography color="success.main" mt={2}>Upload completed successfully!</Typography>}
          {uploadStatus === 'error' && <Typography color="error.main" mt={2}>Failed to process rows. Ensure you have the headers on the first line.</Typography>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleFileUpload} disabled={loading}>
          {loading ? 'Uploading...' : 'Upload'}
        </Button>
      </DialogActions>
      {error && <p>Error occurred while uploading: {error.message}</p>}
    </Dialog>
  )
}

// Export AddSentenceDialog component
export default AddSentencesExcelDialog
